import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import Sparkline from './Sparkline'
import { ink, line, radius, shadow, font, transition } from 'features/dashboard/tokens'

const arrow = trend => (trend > 0 ? '▲' : trend < 0 ? '▼' : '■')

// Count-up animation for the numeric portion of `value`. Strings like
// "3/24" are split, only the leading number counts up. requestAnimationFrame
// keeps the easing smooth without a dep.
const useCountUp = (target, duration = 600) => {
  const [display, setDisplay] = useState(typeof target === 'number' ? 0 : target)
  const startRef = useRef(null)
  const fromRef  = useRef(0)

  useEffect(() => {
    if (typeof target !== 'number' || Number.isNaN(target)) {
      setDisplay(target)
      return
    }
    fromRef.current = typeof display === 'number' ? display : 0
    startRef.current = null

    let raf = 0
    const tick = ts => {
      if (startRef.current === null) startRef.current = ts
      const elapsed = ts - startRef.current
      const t = Math.min(elapsed / duration, 1)
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3)
      const next = fromRef.current + (target - fromRef.current) * eased
      setDisplay(t < 1 ? next : target)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target])

  return display
}

const renderValue = (raw, animated) => {
  if (typeof raw === 'number') {
    return Number.isInteger(raw) ? Math.round(animated) : (Math.round(animated * 10) / 10).toFixed(1)
  }
  // Strings: try to count up the leading numeric portion, leave suffix as-is.
  if (typeof raw === 'string') {
    const match = raw.match(/^(\d+(\.\d+)?)(.*)$/)
    if (match) {
      const num = parseFloat(match[1])
      const tail = match[3]
      if (Number.isFinite(num)) {
        return `${Number.isInteger(num) ? Math.round(animated) : (Math.round(animated * 10) / 10).toFixed(1)}${tail}`
      }
    }
    return raw
  }
  return raw ?? '—'
}

const KpiTile = ({ label, value, unit, trend, trendHint, sparkline, dark }) => {
  // Pull out leading numeric for count-up; otherwise pass-through.
  const numericTarget = typeof value === 'number'
    ? value
    : (typeof value === 'string' && value.match(/^(\d+(\.\d+)?)/))
      ? parseFloat(value.match(/^(\d+(\.\d+)?)/)[1])
      : null
  const animated = useCountUp(numericTarget ?? 0)
  const display  = numericTarget !== null ? renderValue(value, animated) : (value ?? '—')

  return (
    <Tile $dark={dark}>
      <Glow $dark={dark} />
      <Label $dark={dark}>{label}</Label>
      <Value $dark={dark}>
        {display}
        {unit ? <Unit $dark={dark}>{unit}</Unit> : null}
      </Value>
      {trend !== undefined && trend !== null ? (
        <TrendRow>
          <Trend $dir={trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat'}>
            {arrow(trend)} {Math.abs(trend)}{unit === '%' ? 'pp' : '%'}
          </Trend>
          {trendHint ? <TrendHint $dark={dark}>{trendHint}</TrendHint> : null}
        </TrendRow>
      ) : trendHint ? (
        <TrendHint $dark={dark}>{trendHint}</TrendHint>
      ) : null}
      {sparkline ? (
        <SparkWrap>
          <Sparkline data={sparkline} colour={dark ? '#FF836B' : '#D24724'} />
        </SparkWrap>
      ) : null}
    </Tile>
  )
}

export default KpiTile

const tileIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Tile = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 22px;
  border-radius: ${radius.md};
  background: ${({ $dark, theme }) => ($dark ? theme.colors.haiti : theme.colors.white)};
  color: ${({ $dark, theme }) => ($dark ? '#fff' : theme.colors.haiti)};
  border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.06)' : line.base)};
  min-height: 130px;
  overflow: hidden;
  transition: ${transition};
  box-shadow: ${shadow.card};
  animation: ${tileIn} 480ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.14)' : line.strong)};
    box-shadow: ${shadow.lift};
  }
`

const Glow = styled.span`
  display: none;
`

const Label = styled.div`
  position: relative; z-index: 1;
  font-size: ${font.caption};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ $dark, theme }) => ($dark ? 'rgba(255,255,255,0.62)' : theme.colors.grey[800])};
  font-weight: 700;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
`

const Value = styled.div`
  position: relative; z-index: 1;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.hero};
  line-height: 1;
  letter-spacing: -1.4px;
  color: inherit;
  font-variant-numeric: tabular-nums;
`

const Unit = styled.span`
  font-size: ${font.emphasis};
  letter-spacing: -0.5px;
  color: ${({ $dark, theme }) => ($dark ? 'rgba(255,255,255,0.55)' : theme.colors.grey[800])};
  margin-left: 4px;
`

const TrendRow = styled.div`
  position: relative; z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
`

const Trend = styled.span`
  font-size: ${font.small};
  font-weight: 800;
  padding: 3px 10px;
  border-radius: ${radius.pill};
  background: ${({ $dir }) =>
    $dir === 'up'   ? 'rgba(51,190,204,0.14)'
    : $dir === 'down' ? 'rgba(204,34,0,0.12)'
    : 'rgba(12,18,41,0.06)'};
  color: ${({ $dir }) =>
    $dir === 'up' ? '#33becc' : $dir === 'down' ? '#b00020' : ink.base};
  border: 1px solid ${({ $dir }) =>
    $dir === 'up'   ? 'rgba(51,190,204,0.22)'
    : $dir === 'down' ? 'rgba(204,34,0,0.18)'
    : line.base};
`

const TrendHint = styled.span`
  position: relative; z-index: 1;
  font-size: ${font.small};
  color: ${({ $dark, theme }) => ($dark ? 'rgba(255,255,255,0.55)' : theme.colors.grey[800])};
`

const SparkWrap = styled.div`
  position: relative; z-index: 1;
  margin-top: auto;
`
